import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { FiX, FiPlus, FiFileText, FiLoader } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import api, { combinedTicker } from '../../../api/api';
import Toggle from '../Toggle';
import { toast } from '../Toast';

interface Props {
    onClose: () => void;
    onSaved: () => void;
}

const MAX_BYTES = 25 * 1024 * 1024;
const TZ = 'Australia/Sydney';
const LOCAL = 'YYYY-MM-DDTHH:mm';

export const size = (bytes: number) => bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

// spec 2:53024 "Panel / Add announcement": slide-over from the right over the Announcements list.
// ponytail: add only; the Edit screen (2:58009) will pass an existing announcement in later
const EditAnnouncement: React.FC<Props> = ({ onClose, onSaved }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [headline, setHeadline] = useState('');
    const [date, setDate] = useState(moment.tz(TZ).format(LOCAL));
    const [priceSensitive, setPriceSensitive] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [dragging, setDragging] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const pick = (f: File | undefined) => {
        if (!f) return;
        if (!/\.pdf$/i.test(f.name)) { toast.error('Only a PDF can be uploaded'); return; }
        if (f.size > MAX_BYTES) { toast.error('The PDF must be 25 MB or smaller'); return; }
        setFile(f);
    };

    const save = async () => {
        const when = moment.tz(date, LOCAL, TZ);
        if (!headline.trim()) { toast.error('Headline is required'); return; }
        if (!when.isValid()) { toast.error('Date & time is required'); return; }
        if (!file) { toast.error('Announcement PDF is required'); return; }
        setSaving(true);
        const announcement = { headline: headline.trim(), date: when.toISOString(), priceSensitive, remotePath: '', localPath: '', videoUrl: '', videoDescription: '', sendHistory: [], thumbnailPageNum: 1 };
        const result = await api().saveAnnouncement(announcement, null, file, combinedTicker(stock.ticker, stock.exchange)).catch(e => ({ success: false, errorMessage: e.message }));
        setSaving(false);
        if (!result.success) { toast.error(`Save failed: ${result.errorMessage}`); return; }
        toast.success('Announcement saved');
        onSaved();
    };

    return <div className="add-announcement-overlay" onClick={onClose}>
        <aside className="add-announcement-panel" role="dialog" aria-modal="true" aria-label="Add an announcement" onClick={e => e.stopPropagation()}>
            <div className="panel-header">
                <div className="t"><strong>Add an announcement</strong><span>{stock?.companyName || stock?.ticker} · times are Sydney (AEST/AEDT)</span></div>
                <button type="button" className="close" aria-label="Close" onClick={onClose}><FiX /></button>
            </div>

            <div className="panel-body">
                <label className="field">
                    <span className="label">Headline <b>*</b> <em>exactly as lodged with the ASX</em></span>
                    <input autoFocus value={headline} onChange={e => setHeadline(e.target.value)} placeholder="e.g. Drilling Update – Gold Mountain" />
                </label>

                <div className="two">
                    <label className="field">
                        <span className="label">Date &amp; time</span>
                        <input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} />
                    </label>
                    <div className="field">
                        <span className="label">Price sensitive</span>
                        <div className="toggle-row"><Toggle checked={priceSensitive} onChange={setPriceSensitive} label="Price sensitive" /><span>{priceSensitive ? 'Yes' : 'No'}</span></div>
                    </div>
                </div>

                <div className="field">
                    <span className="label">Announcement PDF <b>*</b></span>
                    {file
                        ? <div className="dropzone has-file">
                            <span className="icon-tile"><FiFileText /></span>
                            <div className="l"><strong>{file.name}</strong></div>
                            <small>{size(file.size)} · <button type="button" className="link" onClick={() => setFile(null)}>remove</button></small>
                        </div>
                        // a label so a click anywhere opens the native picker
                        : <label className={`dropzone${dragging ? ' over' : ''}`}
                            onDragOver={e => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={e => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files[0]); }}>
                            <input type="file" accept=".pdf,application/pdf" onChange={e => pick(e.target.files?.[0])} />
                            <span className="icon-tile"><FiPlus /></span>
                            <div className="l">Drop the PDF here or <span className="link">browse files</span></div>
                            <small>PDF up to 25 MB · thumbnail is made from page 1</small>
                        </label>}
                </div>
            </div>

            <div className="panel-footer">
                <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
                <button type="button" className="btn-primary" onClick={save} disabled={saving}>{saving && <FiLoader className="spin" />} Save Announcement</button>
            </div>
        </aside>
    </div>;
};

export default EditAnnouncement;
